import React from "react";
import { Tooltip } from "@mui/material";
import type { ProductKind, FeatureScope } from "./state";

const pillStyle = (active: boolean): React.CSSProperties => ({
    width: 168,
    border: 0,
    borderRadius: 999,
    padding: "6px 14px",
    fontSize: 11.5,
    fontWeight: 600,
    lineHeight: 1.5,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    cursor: "pointer",
    background: active ? "var(--np-brand)" : "var(--bg-input)",
    color: active ? "#fff" : "var(--fg3)"
});

const options: Array<{
    id: "new" | "multi" | "single";
    label: string;
    help: string;
    kind: ProductKind;
    scope: FeatureScope | null;
}> = [
    {
        id: "new",
        label: "New Product",
        help: "Creating something new. We will help define all of the product's features, stories, specifications, and code.",
        kind: "new",
        scope: null
    },
    {
        id: "multi",
        label: "New Features",
        help: "Add new features to an application you are already building. We will help define those features, their stories, specifications, and code.",
        kind: "existing",
        scope: "multi"
    },
    {
        id: "single",
        label: "New Single Feature",
        help: "If you know the one feature you want to add to an existing application, we will help define its stories, specifications, and code.",
        kind: "existing",
        scope: "single"
    }
];

export function KindPills({
    kind,
    scope,
    locked,
    creationExperience = "product",
    onKind,
    onScope,
    onCancel
}: {
    kind: ProductKind | null;
    scope: FeatureScope | null;
    locked: boolean;
    creationExperience?: "product" | "standalone-context-mesh";
    onKind: (k: ProductKind) => void;
    onScope: (s: FeatureScope) => void;
    onCancel: () => void;
}) {
    const selected = kind === "new" ? "new" : kind === "existing" ? scope : null;
    const select = (option: (typeof options)[number]) => {
        onKind(option.kind);
        if (option.scope) onScope(option.scope);
    };
    const helpFor = (option: (typeof options)[number]) => {
        if (creationExperience === "product") return option.help;
        if (option.id === "new") {
            return "Capture context for something new. This creates a standalone Context Mesh without generating a product, features, user stories, or work products.";
        }
        if (option.id === "multi") {
            return "Capture context for multiple proposed features in a standalone Context Mesh without generating the features, user stories, or work products.";
        }
        return "Capture context for one proposed feature in a standalone Context Mesh without generating the feature, user stories, or work products.";
    };

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
            {options.map(option => (
                <span key={option.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <button
                        type="button"
                        aria-pressed={selected === option.id}
                        onClick={() => select(option)}
                        style={pillStyle(selected === option.id)}
                    >
                        {option.label}
                    </button>
                    <Tooltip title={helpFor(option)} arrow enterTouchDelay={0}>
                        <button
                            type="button"
                            aria-label={`About ${option.label}`}
                            style={{
                                width: 20,
                                height: 20,
                                padding: 0,
                                border: "1px solid var(--divider)",
                                borderRadius: "50%",
                                background: "var(--bg-paper)",
                                color: "var(--fg3)",
                                fontSize: 11,
                                fontWeight: 750,
                                cursor: "help"
                            }}
                        >
                            i
                        </button>
                    </Tooltip>
                </span>
            ))}
            {locked && kind === null && (
                <>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            whiteSpace: "nowrap",
                            fontSize: 11.5,
                            fontWeight: 600,
                            padding: "5px 14px",
                            borderRadius: 999,
                            cursor: "pointer",
                            background: "var(--bg-paper)",
                            border: "1px solid var(--divider)",
                            color: "var(--fg3)"
                        }}
                    >
                        Cancel
                    </button>
                    <span style={{ fontSize: 11.5, fontWeight: 650, color: "var(--np-brand)" }}>
                        {"←"} Pick one to unlock the form
                    </span>
                </>
            )}
        </div>
    );
}
